import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { hierarchy, treemap, treemapSquarify } from 'd3-hierarchy'
import { useCampaignData } from "@/contexts/campaign-data"
import { useAccounts } from "@/contexts/account-context"
import { SHEET_TABS } from "@/lib/constants"
import { LoadingSpinner } from "@/components/ui/loading"
import { Button } from '@/components/ui/button'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select"

// Enhanced data structure types
interface DataNode {
    id: string
    name: string
    fullName: string
    value: number
    change: number
    level: 'campaign' | 'adgroup' | 'keyword'
    children?: DataNode[]
}

interface LayoutNode extends DataNode {
    x: number
    y: number
    width: number
    height: number
    color: string
    parent?: string
}

interface BreadcrumbItem {
    id: string
    name: string
    fullPath: string[]
}

interface CurrentView {
    level: 'campaigns' | 'adgroups' | 'keywords'
    title: string
    path: string[]
    breadcrumbs: BreadcrumbItem[]
}

const GoogleAdsPerformanceMap = () => {
    const { data, loadingStatus } = useCampaignData()
    const { activeAccount } = useAccounts()
    const accountCurrency = activeAccount?.currency || '$'
    const containerRef = useRef<HTMLDivElement>(null)
    const svgRef = useRef<SVGSVGElement>(null)

    // State
    const [selectedMetric, setSelectedMetric] = useState<string>('impr')
    const [dateRange, setDateRange] = useState<string>('30')
    const [currentView, setCurrentView] = useState<CurrentView>({
        level: 'campaigns',
        title: 'Google Ads Campaigns',
        path: [],
        breadcrumbs: []
    })
    const [dimensions, setDimensions] = useState({ width: 0, height: 0 })
    const [layout, setLayout] = useState<LayoutNode[]>([])

    // Process raw data into hierarchical structure
    const hierarchicalData = useMemo(() => {
        const rows = data?.[SHEET_TABS.KEYWORD80] || []
        if (!rows.length) return []

        const campaigns = new Map<string, DataNode>()

        rows.forEach(row => {
            const campaignName = row.campaign || ''
            const adGroupName = row.adGroup || ''
            const keywordText = row.keywordText || ''
            const metricValue = Math.max(row[selectedMetric] || 0, 0)
            if (metricValue <= 0) return

            // Campaign level
            if (!campaigns.has(campaignName)) {
                campaigns.set(campaignName, {
                    id: campaignName,
                    name: campaignName,
                    fullName: campaignName,
                    value: 0,
                    change: Math.floor(Math.random() * 40) - 15,
                    level: 'campaign',
                    children: []
                })
            }
            const campaign = campaigns.get(campaignName)!
            campaign.value += metricValue

            // AdGroup level
            const agId = `${campaignName}|${adGroupName}`
            let adGroup = campaign.children!.find(ag => ag.id === agId)
            if (!adGroup) {
                adGroup = {
                    id: agId,
                    name: adGroupName,
                    fullName: adGroupName,
                    value: 0,
                    change: Math.floor(Math.random() * 40) - 15,
                    level: 'adgroup',
                    children: []
                }
                campaign.children!.push(adGroup)
            }
            adGroup.value += metricValue

            // Keyword level
            adGroup.children!.push({
                id: `${agId}|${keywordText}`,
                name: keywordText,
                fullName: keywordText,
                value: metricValue,
                change: Math.floor(Math.random() * 40) - 15,
                level: 'keyword'
            })
        })

        // sort and filter out zero‑value nodes
        const sortByValue = (nodes: DataNode[]): DataNode[] =>
            nodes
                .filter(n => n.value > 0)
                .sort((a, b) => b.value - a.value)
                .map(n => ({
                    ...n,
                    children: n.children ? sortByValue(n.children) : []
                }))

        const result = sortByValue(Array.from(campaigns.values()))

        console.log(
            '%c[hierarchicalData] campaigns=%d, sample ids=%o',
            'color: navy; font-weight:bold',
            result.length,
            result.slice(0, 5).map(c => c.id)
        )

        return result
    }, [data, selectedMetric])

    // Initialize dimensions and handle resize
    useEffect(() => {
        if (!containerRef.current) return;

        const handleResize = () => {
            const containerWidth = containerRef.current?.offsetWidth || 0;
            // Use a fixed height or calculate based on window
            const containerHeight = window.innerHeight - 200; // Adjust as needed

            console.log(
                "%c[dimensions updated]",
                "color: blue; font-weight: bold",
                { width: containerWidth, height: containerHeight }
            );

            setDimensions({
                width: containerWidth,
                height: containerHeight
            });
        };

        // Call immediately to set initial dimensions
        handleResize();

        // Add resize listener
        window.addEventListener('resize', handleResize);

        // Clean up
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [containerRef.current]); // Re-run if container reference changes

    // Add this component before the main component's return statement
    const CampaignNode = ({ node, formatValue }: { node: LayoutNode, formatValue: (value: number) => string }) => {
        const textColor = getTextColor(node.color);
        const isTopCampaign = node.level === 'campaign' && currentView.level === 'campaigns';

        // Dynamically calculate font size based on available space
        const fontSize = Math.min(
            Math.max(Math.floor(node.width / 20), 9),
            14
        );

        // More aggressive space checks
        const hasNameSpace = node.width > 40 && node.height > 25;
        const hasValueSpace = node.width > 40 && node.height > 45;
        const hasChangeSpace = node.width > 40 && node.height > 65;

        // Truncate text more aggressively for small boxes
        const getDisplayName = () => {
            const maxChars = Math.max(Math.floor(node.width / 8), 5);
            if (node.name.length > maxChars) {
                return node.name.substring(0, maxChars) + '...';
            }
            return node.name;
        };

        return (
            <g
                onClick={() => handleNodeClick(node)}
                style={{ cursor: node.level !== 'keyword' ? 'pointer' : 'default' }}
            >
                <rect
                    x={node.x}
                    y={node.y}
                    width={node.width}
                    height={node.height}
                    fill={node.color}
                    stroke="#ffffff"
                    strokeWidth={0.5}
                />

                {isTopCampaign && (
                    // wrap in <a> so it's keyboard‑focusable & semantic
                    <a
                        href="#"
                        onClick={e => {
                            e.stopPropagation();
                            navigateTo([node.id], 'adgroups');
                        }}
                    >
                        <text
                            x={node.x + 4}
                            y={node.y + 14}
                            fontSize="12"
                            fontWeight="bold"
                            fill="white"
                        >
                            {getDisplayName()}
                        </text>
                    </a>
                )}

                {hasNameSpace && !isTopCampaign && (
                    <text
                        x={node.x + 5}
                        y={node.y + 14}
                        fill={textColor}
                        fontSize={fontSize}
                        fontWeight="bold"
                        textAnchor="start"
                        dominantBaseline="hanging"
                    >
                        {getDisplayName()}
                    </text>
                )}

                {hasValueSpace && (
                    <text
                        x={node.x + 5}
                        y={node.y + 14 + (hasNameSpace ? fontSize + 4 : 0)}
                        fill={textColor}
                        fontSize={fontSize}
                        textAnchor="start"
                        dominantBaseline="hanging"
                    >
                        {formatValue(node.value)}
                    </text>
                )}

                {hasChangeSpace && (
                    <text
                        x={node.x + 5}
                        y={node.y + 14 + (hasNameSpace ? fontSize + 4 : 0) + (hasValueSpace ? fontSize + 4 : 0)}
                        fill={textColor}
                        fontSize={fontSize}
                        textAnchor="start"
                        dominantBaseline="hanging"
                    >
                        {node.change > 0 ? '+' : ''}{node.change}%
                    </text>
                )}
            </g>
        );
    };

    // Color functions
    const getColorForChange = useCallback((change: number): string => {
        // Red scale for negative
        if (change <= -15) return '#C62828'; // dark red
        if (change <= -10) return '#D32F2F';
        if (change <= -5) return '#E53935';
        if (change < 0) return '#EF5350'; // light red

        // Green scale for positive
        if (change >= 15) return '#2E7D32'; // dark green
        if (change >= 10) return '#388E3C';
        if (change >= 5) return '#43A047';
        return '#66BB6A'; // light green
    }, []);

    const getTextColor = useCallback((bgColorHex: string): string => {
        // All our colors use white text for consistency and readability
        return 'white';
    }, []);

    // Calculate layout when view or dimensions change
    useEffect(() => {
        console.log(
            "%c[layout effect fired]",
            "color: purple; font-weight:bold",
            { dimensions, hierarchicalDataLength: hierarchicalData.length, currentView }
        );

        if (!dimensions.width || !hierarchicalData.length) {
            console.log("%c[layout effect skipped—no data or zero dims]", "color: gray");
            return;
        }

        const padding = 4

        // pick the items & children based on view
        let outerItems: DataNode[]
        let getChildren: (n: DataNode | LayoutNode) => DataNode[] // Allow LayoutNode for getChildren

        if (currentView.level === 'campaigns') {
            outerItems = hierarchicalData
            // Need to find the original DataNode to get children
            getChildren = (n: DataNode | LayoutNode) => {
                const originalNode = hierarchicalData.find(item => item.id === n.id);
                return originalNode?.children || [];
            }
        } else {
            const [cid] = currentView.path
            const campaign = hierarchicalData.find(c => c.id === cid)
            outerItems = campaign?.children || []
            // Need to find the original AdGroup DataNode to get children (keywords)
            getChildren = (n: DataNode | LayoutNode) => {
                const parentCampaign = hierarchicalData.find(c => c.id === cid);
                const originalNode = parentCampaign?.children?.find(item => item.id === n.id);
                return originalNode?.children || [];
            }
        }

        console.log(
            "%c[layout effect] currentView=", "color: purple; font-weight:bold",
            currentView, "outerItems:", outerItems.map(o => o.id)
        );

        // helper to run a one‑level D3 treemap and return LayoutNodes
        function runTreemap(
            nodes: DataNode[],
            width: number,
            height: number,
            returnLeaves: boolean
        ): LayoutNode[] {
            console.log(
                `%c[runTreemap] level=%s, count=%d, size=%dx%d`,
                "color: teal; font-weight:bold",
                returnLeaves ? "inner" : "outer",
                nodes.length,
                width,
                height
            );

            const dummyRoot: DataNode = { id: 'root', name: 'root', fullName: 'root', value: 0, change: 0, level: 'campaign', children: nodes };
            const root = hierarchy<DataNode>(dummyRoot)
                .sum(d => d.value)
                .sort((a, b) => (b.value ?? 0) - (a.value ?? 0));

            treemap<DataNode>()
                .size([width, height])
                .paddingInner(4)
                .tile(treemapSquarify)
                (root);

            // pick children vs leaves
            const work = returnLeaves ? root.leaves() : (root.children || []);
            // Filter out the dummy root if it appears (relevant for children case)
            const layoutNodes = work.filter(node => node.data.id !== 'root').map(leaf => {
                // Assert that the layout properties exist after treemap calculation
                const n = leaf as d3.HierarchyRectangularNode<DataNode>;
                return {
                    id: n.data.id,
                    name: n.data.name,
                    fullName: n.data.fullName,
                    value: n.value!, // Use the value calculated by d3 (.sum)
                    change: n.data.change,
                    level: n.data.level,
                    x: n.x0,
                    y: n.y0,
                    width: n.x1 - n.x0,
                    height: n.y1 - n.y0,
                    color: getColorForChange(n.data.change)
                }
            });

            console.log(
                `%c[runTreemap → returned] count=%d`,
                "color: orange; font-weight:bold",
                layoutNodes.length
            );
            console.table(layoutNodes.slice(0, 5)); // show first few boxes

            return layoutNodes;
        }

        // 1) layout the "outer" layer across full canvas
        const outerRects = runTreemap(outerItems, dimensions.width, dimensions.height, /* returnLeaves= */ false);
        console.log(
            "%c[outerRects] total=", "color: green; font-weight:bold",
            outerRects.length
        );

        // 2) for each outer rect, inset by padding*2 and layout its children inside
        const nested: LayoutNode[] = []
        outerRects.forEach(camp => {
            nested.push(camp)

            const kids = getChildren(camp)
            const padding = 4; // Define padding here for clarity or use the value from above
            if (
                kids.length &&
                camp.width > padding * 2 &&
                camp.height > padding * 2
            ) {
                const innerWidth = camp.width - padding * 2
                const innerHeight = camp.height - padding * 2

                const innerReturnLeaves = currentView.level === 'campaigns'
                    // we want the *direct children* of each campaign (i.e. ad‑groups)
                    ? false
                    // (when zoomed into one campaign) we want the *leaves* of each ad‑group (i.e. keywords)
                    : true

                const inner = runTreemap(
                    kids,
                    innerWidth,
                    innerHeight,
                    innerReturnLeaves // Use the view-dependent flag
                ).map(k => ({
                    ...k,
                    x: k.x + camp.x + padding,
                    y: k.y + camp.y + padding
                }))
                console.log(
                    `%c[innerRects for %s] count=`, "color: magenta;",
                    camp.id, inner.length
                )

                nested.push(...inner)
            }
        })

        console.log("%c[final layout] total nodes=", "color: red;", nested.length);
        setLayout(nested)
    }, [
        dimensions,
        hierarchicalData,
        currentView,
        getColorForChange
    ])

    // Navigation handlers
    const navigateTo = useCallback((path: string[], level: 'campaigns' | 'adgroups' | 'keywords') => {
        // Build breadcrumbs based on path
        const breadcrumbs: BreadcrumbItem[] = [];

        if (path.length > 0) {
            // Add "All Campaigns" as the first breadcrumb
            breadcrumbs.push({
                id: 'root',
                name: 'All Campaigns',
                fullPath: []
            });

            // Add campaign if needed
            if (path.length >= 1) {
                const campaignId = path[0];
                const campaign = hierarchicalData.find(c => c.id === campaignId);
                if (campaign) {
                    breadcrumbs.push({
                        id: campaign.id,
                        name: campaign.name,
                        fullPath: [campaign.id]
                    });
                }
            }

            // Add ad group if needed
            if (path.length >= 2) {
                const campaignId = path[0];
                const adGroupId = path[1];
                const campaign = hierarchicalData.find(c => c.id === campaignId);
                if (campaign && campaign.children) {
                    const adGroup = campaign.children.find(ag => ag.id === adGroupId);
                    if (adGroup) {
                        breadcrumbs.push({
                            id: adGroup.id,
                            name: adGroup.name,
                            fullPath: [campaign.id, adGroup.id]
                        });
                    }
                }
            }
        }

        // Determine title based on level
        let title = 'Google Ads Campaigns';
        if (level === 'adgroups' && path.length > 0) {
            const campaign = hierarchicalData.find(c => c.id === path[0]);
            if (campaign) {
                title = `Campaign: ${campaign.name}`;
            }
        } else if (level === 'keywords' && path.length > 1) {
            const campaign = hierarchicalData.find(c => c.id === path[0]);
            if (campaign && campaign.children) {
                const adGroup = campaign.children.find(ag => ag.id === path[1]);
                if (adGroup) {
                    title = `Ad Group: ${adGroup.name}`;
                }
            }
        }

        setCurrentView({
            level,
            title,
            path,
            breadcrumbs
        });
    }, [hierarchicalData]);

    // Handle clicking on a node
    const handleNodeClick = useCallback((node: LayoutNode) => {
        if (node.level === 'campaign') {
            navigateTo([node.id], 'adgroups');
        } else if (node.level === 'adgroup') {
            // Extract campaign ID from the ad group ID
            const campaignId = node.id.split('|')[0];
            navigateTo([campaignId, node.id], 'keywords');
        }
        // Keywords are leaf nodes, no action needed
    }, [navigateTo]);

    // Format value based on the selected metric
    const formatValue = useCallback((value: number): string => {
        if (selectedMetric === 'cost') {
            return `${accountCurrency}${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
        } else if (selectedMetric === 'conv' || selectedMetric === 'value') {
            return value.toLocaleString(undefined, { maximumFractionDigits: 1 });
        } else {
            return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
        }
    }, [selectedMetric, accountCurrency]);

    // Loading state
    if (loadingStatus === 'initial' || loadingStatus === 'refresh') {
        return (
            <div className="flex items-center justify-center h-screen">
                <LoadingSpinner />
            </div>
        );
    }

    // Empty data state
    if (hierarchicalData.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-screen">
                <h2 className="text-xl font-bold mb-4">No data available</h2>
                <p>No search campaign data found to display in the performance map.</p>
            </div>
        );
    }

    return (
        <div className="container-fluid p-0 max-w-full" ref={containerRef}>
            <div className="flex flex-col w-full bg-gray-100">
                {/* Header */}
                <div className="flex items-center justify-between p-4 bg-white">
                    <h1 className="text-2xl font-bold">Google Ads Performance Map</h1>

                    <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-2">
                            <span>Metric:</span>
                            <Select
                                value={selectedMetric}
                                onValueChange={setSelectedMetric}
                            >
                                <SelectTrigger className="w-36">
                                    <SelectValue placeholder="Select Metric" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="impr">Impressions</SelectItem>
                                    <SelectItem value="clicks">Clicks</SelectItem>
                                    <SelectItem value="cost">Cost</SelectItem>
                                    <SelectItem value="conv">Conversions</SelectItem>
                                    <SelectItem value="value">Conversion Value</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="flex items-center space-x-2">
                            <span>Period:</span>
                            <Select
                                value={dateRange}
                                onValueChange={setDateRange}
                            >
                                <SelectTrigger className="w-36">
                                    <SelectValue placeholder="Select Period" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="7">Last 7 Days</SelectItem>
                                    <SelectItem value="30">Last 30 Days</SelectItem>
                                    <SelectItem value="90">Last 90 Days</SelectItem>
                                    <SelectItem value="year">Year to Date</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </div>

                {/* Breadcrumb Navigation */}
                <div className="flex items-center p-4 bg-white border-t border-b">
                    {currentView.breadcrumbs.map((crumb, index) => (
                        <div key={crumb.id} className="flex items-center">
                            {index > 0 && <span className="mx-2 text-gray-400">/</span>}
                            <button
                                className={`text-sm ${index === currentView.breadcrumbs.length - 1 ? 'font-bold text-blue-600' : 'text-gray-600 hover:text-blue-500'}`}
                                onClick={() => {
                                    if (index === 0) {
                                        navigateTo([], 'campaigns');
                                    } else {
                                        const level = index === 1 ? 'adgroups' : 'keywords';
                                        navigateTo(crumb.fullPath, level as any);
                                    }
                                }}
                            >
                                {crumb.name}
                            </button>
                        </div>
                    ))}
                </div>

                {/* Performance Map */}
                <div className="flex-1 overflow-auto">
                    <svg
                        ref={svgRef}
                        className="w-full h-full"
                        viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
                        preserveAspectRatio="xMidYMid meet"
                    >
                        {/* Render campaigns first */}
                        {layout.map((node, index) => (
                            <CampaignNode key={index} node={node} formatValue={formatValue} />
                        ))}
                    </svg>
                </div>
            </div>
        </div>
    );
}

export default GoogleAdsPerformanceMap;